import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { TaskDetailsDialog } from "./TaskDetailsDialog";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { TaskCard } from "./TaskCard";
import { TaskFilters } from "./TaskFilters";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Button } from "../ui/button";

interface TaskListProps {
  leadId?: string;
}

export const TaskList = ({ leadId }: TaskListProps) => {
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("todo");
  const [filterUser, setFilterUser] = useState<string>("all");
  const [userRole, setUserRole] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  // Set current user as default filter on mount
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setFilterUser(user.id);
      }
    };
    getCurrentUser();
  }, []);

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        if (profile) {
          setUserRole(profile.role);
        }
      }
    };
    fetchUserRole();
  }, []);

  const { data: tasks, isLoading, error } = useQuery({
    queryKey: ["tasks", leadId, filterStatus, filterUser, currentOrganization?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user");
      if (!currentOrganization) throw new Error("No organization selected");

      console.log("Fetching tasks for organization:", currentOrganization.id);

      const { data, error } = await supabase.functions.invoke('tasks-operations', {
        body: {
          action: 'get',
          organizationId: currentOrganization.id,
          filters: {
            status: filterStatus !== "all" ? filterStatus : undefined,
            assignedTo: filterUser !== "all" ? filterUser : undefined,
            leadId: leadId
          }
        }
      });
      
      if (error) {
        console.error("Error fetching tasks:", error);
        throw error;
      }
      
      console.log("Tasks fetched:", data?.tasks?.length || 0);
      return data?.tasks || [];
    },
    enabled: !!currentOrganization,
  });

  const handleDelete = async () => {
    if (!taskToDelete?.id || isDeleting || !currentOrganization) return;

    try {
      setIsDeleting(true);
      setShowDeleteConfirm(false);

      const { error } = await supabase.functions.invoke("tasks-operations", {
        body: {
          action: "delete",
          taskId: taskToDelete.id,
          organizationId: currentOrganization.id
        }
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      
      toast({
        title: "Success",
        description: "Task deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: "Error",
        description: "Failed to delete task",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setTaskToDelete(null);
    }
  };

  const handleTaskClick = (task: any) => {
    setSelectedTask(task);
    setIsDetailsOpen(true);
  };

  // Subscribe to real-time changes
  useEffect(() => {
    if (!currentOrganization) return;
    
    console.log('Setting up real-time subscription for tasks');
    const channel = supabase
      .channel('tasks-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks',
          filter: leadId 
            ? `lead_id=eq.${leadId} AND organization_id=eq.${currentOrganization.id}` 
            : `organization_id=eq.${currentOrganization.id}`
        },
        (payload) => {
          console.log('Received task change:', payload);
          queryClient.invalidateQueries({ queryKey: ['tasks', leadId, filterStatus, filterUser, currentOrganization.id] });
        }
      )
      .subscribe((status) => {
        console.log('Task subscription status:', status);
      });

    return () => {
      console.log('Cleaning up tasks subscription');
      supabase.removeChannel(channel);
    };
  }, [queryClient, leadId, filterStatus, filterUser, currentOrganization]);

  if (!currentOrganization) {
    return (
      <div className="flex flex-col items-center justify-center p-8 border rounded-lg bg-muted/50">
        <h3 className="text-lg font-medium mb-2">No Organization Selected</h3>
        <p className="text-muted-foreground mb-4">Please select an organization to view tasks.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 border rounded-lg bg-destructive/10">
        <h3 className="text-lg font-medium mb-2">Error Loading Tasks</h3>
        <p className="text-muted-foreground mb-4">{(error as Error).message}</p>
        <Button onClick={() => queryClient.invalidateQueries({ queryKey: ["tasks"] })}>
          Retry
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
        <p className="text-muted-foreground">Loading tasks...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <TaskFilters
        filterStatus={filterStatus}
        filterUser={filterUser}
        onStatusChange={setFilterStatus}
        onUserChange={setFilterUser}
      />

      {tasks && tasks.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onTaskClick={handleTaskClick}
              onDeleteClick={(task) => {
                setTaskToDelete(task);
                setShowDeleteConfirm(true);
              }}
              userRole={userRole}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center p-8 border rounded-lg bg-muted/50">
          <h3 className="text-lg font-medium mb-2">No Tasks Found</h3>
          <p className="text-muted-foreground">
            {filterStatus !== "all" || filterUser !== "all" 
              ? "Try changing your filters to see more tasks." 
              : "Create a new task to get started."}
          </p>
        </div>
      )}

      <TaskDetailsDialog
        task={selectedTask}
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        userRole={userRole}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleDelete}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
      />
    </div>
  );
};
